import React, { useState } from 'react';
import { FileCheck, ArrowRight, ArrowLeft, AlertCircle } from 'lucide-react';
import { CertifiedNetworkApplication } from '../../services/certifiedNetworkService';
import LoadingButton from '../LoadingButton';

interface Step6ObligationsProps {
  data: Partial<CertifiedNetworkApplication>;
  onComplete: (data: any) => void;
  onBack: () => void;
}

const Step6Obligations: React.FC<Step6ObligationsProps> = ({ data, onComplete, onBack }) => {
  const [formData, setFormData] = useState({
    collaborationObligations: data.collaborationObligations || {
      accepted: false,
      acceptedAt: undefined
    },
    declaration: data.declaration || {
      accepted: false,
      signerName: '',
      signedAt: undefined
    }
  });

  const [errors, setErrors] = useState<any>({});

  const validate = () => {
    const newErrors: any = {};

    if (!formData.collaborationObligations.accepted) {
      newErrors.obligations = 'Devi accettare gli obblighi di collaborazione';
    }

    if (!formData.declaration.accepted) {
      newErrors.declaration = 'Devi accettare la dichiarazione di veridicità';
    }

    if (!formData.declaration.signerName.trim()) {
      newErrors.signerName = 'Inserisci nome e cognome del firmatario';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validate()) {
      const dataToSubmit = {
        ...formData,
        collaborationObligations: {
          ...formData.collaborationObligations,
          acceptedAt: new Date()
        },
        declaration: {
          ...formData.declaration,
          signedAt: new Date()
        }
      };
      onComplete(dataToSubmit);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-100 dark:bg-teal-900/50 rounded-full mb-4">
          <FileCheck className="w-8 h-8 text-teal-600 dark:text-teal-400" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Obblighi e Dichiarazioni
        </h2>
        <p className="text-gray-600 dark:text-gray-300">
          Leggi attentamente e accetta gli obblighi di collaborazione e la dichiarazione di veridicità.
        </p>
      </div>

      {/* Sezione 13: Obblighi di Collaborazione */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b dark:border-gray-700 pb-2">
          13. Obblighi di Collaborazione all'interno della "Rete Certificata UNASCA"
        </h3>

        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 space-y-4 text-sm text-gray-700 dark:text-gray-300">
          <p>
            Come aderente alla Rete Certificata UNASCA, lo Studio di Consulenza si impegna a 
            rispettare il Regolamento della Rete Certificata ed a collaborare attivamente con 
            gli altri membri della rete per migliorare l'efficienza, la qualità dei servizi e 
            la soddisfazione dei clienti.
          </p>

          <p className="font-medium">Tale collaborazione è obbligatoria e si estende ai seguenti ambiti:</p>

          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>
              <strong>Svincolo degli atti:</strong> L'aderente si impegna a collaborare nel 
              processo di svincolo degli atti con le altre agenzie della rete, garantendo che 
              i documenti siano trasferiti rapidamente e in modo corretto.
            </li>
            <li>
              <strong>Acquisto e scambio di crediti PagoPA:</strong> Lo Studio si coordinerà
              con le altre agenzie della rete per ottimizzare l'acquisto e lo scambio di crediti PagoPA
              secondo quanto previsto dal punto 4.4 del Regolamento.
            </li>
            <li>
              <strong>Supporto reciproco per la gestione dei rimborsi:</strong> Lo Studio 
              parteciperà alla gestione congiunta dei rimborsi crediti, garantendo che i rimborsi 
              non utilizzati possano essere facilmente riutilizzati dagli altri membri della rete.
            </li>
            <li>
              <strong>Versamento della quota annuale di certificazione:</strong> L'aderente si 
              impegna a versare regolarmente la quota annuale di certificazione stabilita dal 
              Comitato di Gestione, condizione essenziale per mantenere la validità della 
              certificazione stessa.
            </li>
            <li>
              <strong>Limitazione all'adesione a piattaforme digitali:</strong> Lo Studio si
              impegna a non aderire a piattaforme digitali che impongano un prezzo finale
              predefinito per le pratiche né a quelle che promuovano direttamente l'espletamento
              della pratica automobilistica nei confronti dell'utenza, configurandosi come
              intermediari del servizio. Tali modalità sono in contrasto con l'identità della
              Rete Certificata, che riconosce il valore consulenziale, l'autonomia operativa e
              la responsabilità diretta dello Studio nei confronti del cittadino.
            </li>
          </ul>

          <p className="text-xs text-gray-600 mt-4">
            L'adesione alla Rete Certificata comporta il rispetto di questi obblighi oltre che 
            agli obblighi di formazione professionale ed in campo di sicurezza informatica e 
            protezione dei dati personali, promuovendo una collaborazione efficace e sinergica 
            tra tutte le agenzie coinvolte.
          </p>
        </div>

        <div className="flex items-start gap-3 p-4 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 rounded-lg">
          <input
            type="checkbox"
            id="acceptObligations"
            checked={formData.collaborationObligations.accepted}
            onChange={(e) => {
              setFormData(prev => ({
                ...prev,
                collaborationObligations: {
                  ...prev.collaborationObligations,
                  accepted: e.target.checked
                }
              }));
              if (errors.obligations) {
                setErrors((prev: any) => {
                  const newErrors = { ...prev };
                  delete newErrors.obligations;
                  return newErrors;
                });
              }
            }}
            className="w-5 h-5 text-green-600 border-gray-300 dark:border-gray-600 rounded focus:ring-green-500 mt-0.5"
          />
          <label htmlFor="acceptObligations" className="text-sm font-medium text-gray-900 dark:text-white">
            Accetto tutti gli obblighi di collaborazione sopra elencati e mi impegno a rispettarli
          </label>
        </div>
        {errors.obligations && (
          <p className="text-sm text-red-600 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {errors.obligations}
          </p>
        )}
      </div>

      {/* Sezione 14: Dichiarazione di Veridicità */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b dark:border-gray-700 pb-2">
          14. Dichiarazione di Veridicità
        </h3>

        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Dichiaro che tutte le informazioni fornite in questa domanda sono veritiere e 
            complete e mi impegno a fornire ulteriori dettagli qualora richiesti dal Comitato 
            di Gestione della Rete Certificata UNASCA.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Nome e Cognome del Legale Rappresentante *
          </label>
          <input
            type="text"
            value={formData.declaration.signerName}
            onChange={(e) => {
              setFormData(prev => ({
                ...prev,
                declaration: {
                  ...prev.declaration,
                  signerName: e.target.value
                }
              }));
              if (errors.signerName) {
                setErrors((prev: any) => {
                  const newErrors = { ...prev };
                  delete newErrors.signerName;
                  return newErrors;
                });
              }
            }}
            placeholder="Mario Rossi"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500"
          />
          {errors.signerName && (
            <p className="mt-1 text-sm text-red-600">{errors.signerName}</p>
          )}
        </div>

        <div className="flex items-start gap-3 p-4 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 rounded-lg">
          <input
            type="checkbox"
            id="acceptDeclaration"
            checked={formData.declaration.accepted}
            onChange={(e) => {
              setFormData(prev => ({
                ...prev,
                declaration: {
                  ...prev.declaration,
                  accepted: e.target.checked
                }
              }));
              if (errors.declaration) {
                setErrors((prev: any) => {
                  const newErrors = { ...prev };
                  delete newErrors.declaration;
                  return newErrors;
                });
              }
            }}
            className="w-5 h-5 text-green-600 border-gray-300 dark:border-gray-600 rounded focus:ring-green-500 mt-0.5"
          />
          <label htmlFor="acceptDeclaration" className="text-sm font-medium text-gray-900 dark:text-white">
            Confermo la veridicità di tutte le informazioni fornite
          </label>
        </div>
        {errors.declaration && (
          <p className="text-sm text-red-600 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {errors.declaration}
          </p>
        )}

        <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
          Data: {new Date().toLocaleDateString('it-IT')}
        </div>
      </div>

      {/* Buttons */}
      <div className="flex justify-between pt-6 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-2 px-6 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
        >
          <ArrowLeft className="w-5 h-5" />
          Indietro
        </button>
        <LoadingButton
          type="submit"
          className="flex items-center gap-2 bg-green-600 text-white px-8 py-2 rounded-lg hover:bg-green-700"
        >
          Continua
          <ArrowRight className="w-5 h-5" />
        </LoadingButton>
      </div>
    </form>
  );
};

export default Step6Obligations;

